import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ISecurityGroupDocument} from '../../api/types/savedsearch.types';
import {ISecurityGroupCountResponse} from '../../api/types/ingestionApi.type';

export type IndexStatus =
  | 'Complete'
  | 'Failed'
  | 'Draft'
  | 'InProgress'
  | undefined;

export interface IRequestField {
  fieldId: number;
  analyzerId: number;
  analyzerKey?: string;
}

export interface ISelectedMetadataFieldName {
  fieldId: number;
  fieldName: string;
  analyzerKey: string;
  analyzerName?: string;
  isDefault: boolean;
  fieldType?: string;
}

export interface ICreateCustomIndex {
  indexName: string;
  securityGroupIds?: number[];
  savedSearchId: number | null;
  fields: IRequestField[];
  notificationEmails: string[];
}

export interface ICreateCustomIndexResponse {
  customIndexID: number;
  indexName: string;
  indexRunStatus: string;
  responseMessage: string;
  createdOn: string;
}

export interface ISelectedDocuments {
  source: string;
  documents: number | string;
  savedSearchSecurityGroup: ISecurityGroupDocument[];
}

export interface INewIndexDetails {
  indexId: number;
  responseMessage: string;
}

export interface IIndexState {
  tabValue: number;
  createCustomIndex: ICreateCustomIndex;
  selectedSecurityGroup: string[];
  selectedDocuments: ISelectedDocuments;
  selectedMetadataFieldNames: ISelectedMetadataFieldName[];
  indexStatus: IndexStatus;
  isReadOnlyView: boolean;
  newIndex: boolean;
  newIndexDetails: INewIndexDetails;
  viewIndexId: number;
  securityGroupCount: ISecurityGroupCountResponse[];
}

const initialState: IIndexState = {
  tabValue: 0,
  createCustomIndex: {
    indexName: '',
    securityGroupIds: [],
    savedSearchId: null,
    fields: [],
    notificationEmails: [],
  },
  selectedSecurityGroup: [],
  selectedDocuments: {
    source: '',
    documents: 0,
    savedSearchSecurityGroup: [],
  },
  selectedMetadataFieldNames: [],
  indexStatus: undefined,
  isReadOnlyView: false,
  newIndex: false,
  newIndexDetails: {
    indexId: 0,
    responseMessage: '',
  },
  viewIndexId: 0,
  securityGroupCount: [],
};

const createCustomIndexSlice = createSlice({
  name: 'indexManagement',
  initialState,
  reducers: {
    setTabValue(state, action: PayloadAction<number>) {
      state.tabValue = action.payload;
    },
    setCreateCustomIndex(
      state,
      action: PayloadAction<Partial<ICreateCustomIndex>>
    ) {
      state.createCustomIndex = {
        ...state.createCustomIndex,
        ...action.payload,
      };
    },
    setIndexName(state, action: PayloadAction<string>) {
      state.createCustomIndex.indexName = action.payload;
    },
    setSecurityGroupIds(state, action: PayloadAction<number[]>) {
      state.createCustomIndex.securityGroupIds = action.payload;
    },
    setSelectedSecurityGroup(state, action: PayloadAction<string[]>) {
      state.selectedSecurityGroup = action.payload;
    },
    setSavedSearchId(state, action: PayloadAction<number>) {
      state.createCustomIndex.savedSearchId = action.payload;
    },
    setFields(state, action: PayloadAction<IRequestField[]>) {
      state.createCustomIndex.fields = action.payload;
    },
    setNewIndex(state, action: PayloadAction<boolean>) {
      state.newIndex = action.payload;
    },
    setNewIndexDetails(state, action: PayloadAction<INewIndexDetails>) {
      state.newIndexDetails = action.payload;
    },
    setSelectedDocument(state, action: PayloadAction<ISelectedDocuments>) {
      state.selectedDocuments = action.payload;
    },
    setSelectedMetadataFieldNames(
      state,
      action: PayloadAction<ISelectedMetadataFieldName[]>
    ) {
      state.selectedMetadataFieldNames = action.payload;
    },
    setNotificationEmails(state, action: PayloadAction<string[]>) {
      state.createCustomIndex.notificationEmails = action.payload;
    },
    setIsReadyOnlyView(state, action: PayloadAction<boolean>) {
      state.isReadOnlyView = action.payload;
    },
    setIndexStatus(state, action: PayloadAction<IndexStatus>) {
      state.indexStatus = action.payload;
    },
    setViewIndexId(state, action: PayloadAction<number>) {
      state.viewIndexId = action.payload;
    },
    resetCustomIndexState() {
      return initialState;
    },
    setSecurityGroupCount(
      state,
      action: PayloadAction<ISecurityGroupCountResponse[]>
    ) {
      state.securityGroupCount = action.payload;
    },
  },
});

export const {
  setTabValue,
  setCreateCustomIndex,
  setIndexName,
  setSecurityGroupIds,
  setSelectedSecurityGroup,
  setNewIndex,
  setNewIndexDetails,
  setSelectedDocument,
  setSavedSearchId,
  setFields,
  setSelectedMetadataFieldNames,
  setNotificationEmails,
  setIsReadyOnlyView,
  resetCustomIndexState,
  setIndexStatus,
  setViewIndexId,
  setSecurityGroupCount,
} = createCustomIndexSlice.actions;

export default createCustomIndexSlice.reducer;
